import { useNode } from "@craftjs/core";
import React, { useState, useMemo, useContext } from "react";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GrigoraDashboardContext } from "../../dashboard/context";
import {
  UnitInput,
  Spacer,
  Tabs,
  AlignInput,
  TextInput,
  ToggleInput,
  SelectInput,
  MultipleSelect,
  EditPopover,
} from "../../input";
import VideoPicker from "../../input/video";
import { supportLibrary } from "../../block-supports";
import { remove_prefix } from "../../helpers";
import { isUpdatedFromDefault } from "../../utils";
import { ImagePickerUIKit, VideoPickerUIKit } from "../../ui-kit";
import { INPUT_LIMITS } from "../../input-limits";

function SourceVideo({ projectID, src, srcMode, onClick }) {
  const videosrc = useMemo(() => {
    if (srcMode === "cdn") {
      return `https://cdn.grigora.co/projects/${projectID}/${remove_prefix(
        src.sk,
        "video-",
      )}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);

  return (
    <div onClick={onClick} className="relative">
      <div className="absolute flex top-0 bottom-0 left-0 right-0 justify-center items-center duration-300 hover:bg-black">
        <FontAwesomeIcon icon={faEdit} />
      </div>
      <video
        className="video-frame"
        width={srcMode === "cdn" ? src.width : undefined}
        height={srcMode === "cdn" ? src.height : undefined}
        src={videosrc}
      ></video>
    </div>
  );
}
function SourceImage({ projectID, src, srcMode, onClick }) {
  const imgsrc = useMemo(() => {
    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${
        src?.projectID ? src.projectID : projectID
      }/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);

  return (
    <>
      <div onClick={onClick} className="relative">
        <div className="absolute flex top-0 bottom-0 left-0 right-0 justify-center items-center duration-300 hover:bg-editor-bg-main">
          <FontAwesomeIcon icon={faEdit} />
        </div>
        <img src={imgsrc} />
      </div>
    </>
  );
}
export const VideoSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    src,
    srcMode,
    posterSrc,
    posterSrcMode,
    title,
    muted = false,
    autoplay = false,
    showControls = true,
    controlsList,
    preload,
    loop,
    disablePictureInPicture,
    width,
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    align,
    alignTablet,
    alignMobile,
  } = props;
  const [srcModal, setSrcModal] = useState(false);
  const [posterSrcModal, setPosterSrcModal] = useState(false);
  const { projectID } = useContext(GrigoraDashboardContext);

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <div className="flex items-center justify-center cursor-pointer text-sm bg-editor-bg-main duration-300 mb-4">
            <SourceVideo
              projectID={projectID}
              src={src}
              srcMode={srcMode}
              onClick={() => {
                setSrcModal(true);
              }}
            />
          </div>
          <VideoPickerUIKit
            src={src}
            srcMode={srcMode}
            onSrcChange={(val) => {
              setProp((props) => {
                props.src = val;
              });
            }}
            onSrcModeChange={(val) => {
              setProp((props) => {
                props.srcMode = val;
              });
            }}
            opened={srcModal}
            setOpened={setSrcModal}
          />
          <EditPopover label={"Poster"} canReset={false}>
            <div>
              <div>
                <h6 className="text-xs my-2">Poster</h6>
                <div className="flex items-center justify-center cursor-pointer text-sm bg-editor-bg-main-hover duration-300 hover:bg-editor-bg-light mb-4">
                  <SourceImage
                    projectID={projectID}
                    src={posterSrc}
                    srcMode={posterSrcMode}
                    onClick={() => {
                      setPosterSrcModal(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </EditPopover>
          <ImagePickerUIKit
            resetValue={undefined}
            src={posterSrc}
            srcMode={posterSrcMode}
            onChange={(src, srcMode) => {
              setProp((props) => {
                props.posterSrc = src;
                props.posterSrcMode = srcMode;
              });
            }}
            opened={posterSrcModal}
            setOpened={setPosterSrcModal}
          />
          <TextInput
            minLength={INPUT_LIMITS["video-title"].minlength}
            maxLength={INPUT_LIMITS["video-title"].maxlength}
            isResponsive={true}
            resetValue={undefined}
            value={title}
            onChange={(val) => {
              setProp((props) => {
                props.title = val;
              });
            }}
            label={"Title"}
          />
          <ToggleInput
            isResponsive={true}
            resetValue={undefined}
            value={muted}
            onChange={(val) => {
              setProp((props) => {
                props.muted = val;
              });
            }}
            label={"Muted"}
          />
          <ToggleInput
            isResponsive={true}
            resetValue={undefined}
            value={autoplay}
            onChange={(val) => {
              setProp((props) => {
                props.autoplay = val;
              });
            }}
            label={"Autoplay"}
          />
          <ToggleInput
            isResponsive={true}
            resetValue={undefined}
            value={showControls}
            onChange={(val) => {
              setProp((props) => {
                props.showControls = val;
              });
            }}
            label={"Show Controls"}
          />{" "}
          <ToggleInput
            isResponsive={true}
            resetValue={undefined}
            value={loop}
            onChange={(val) => {
              setProp((props) => {
                props.loop = val;
              });
            }}
            label={"Loop"}
          />
          <ToggleInput
            isResponsive={true}
            resetValue={undefined}
            value={disablePictureInPicture}
            onChange={(val) => {
              setProp((props) => {
                props.disablePictureInPicture = val;
              });
            }}
            label={"Disable Picture In Picture"}
          />
          <MultipleSelect
            resetValue={undefined}
            value={controlsList}
            onChange={(val) => {
              setProp((props) => {
                props.controlsList = val;
              });
            }}
            options={[
              {
                label: "No Full Screen",
                value: "nofullscreen",
              },
              {
                label: "No Download",
                value: "nodownload",
              },
              {
                label: "No Remote Playback",
                value: "noremoteplayback",
              },
              {
                label: "No Playback Rate",
                value: "noplaybackrate",
              },
              {
                label: "Foobar",
                value: "foobar",
              },
            ]}
            label="Control List"
          />
          <SelectInput
            resetValue={undefined}
            value={preload}
            onChange={(val) => {
              setProp((props) => {
                props.preload = val;
              });
            }}
            options={[
              {
                label: "None",
                value: "none",
              },
              {
                label: "Metadata",
                value: "metadata",
              },
              {
                label: "Auto",
                value: "auto",
              },
            ]}
            label={"Preload"}
          />
          <AlignInput
            value={align}
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <UnitInput
            auto={true}
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            auto={true}
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
        </Spacer>
      </div>
    );
  }
  function AdvancedSettings() {
    return (
      <>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: widthMobile },
        { key: widthTablet },
        { key: width },
        { key: alignMobile },
        { key: alignTablet },
        { key: align },
        { key: preload },
        { key: controlsList },
        { key: disablePictureInPicture },
        { key: loop },
        { key: showControls },
        { key: autoplay },
        { key: muted },
        { key: title },
        { key: posterSrcMode },
        { key: posterSrc },
        { key: srcMode },
        { key: projectID },
        { key: src },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div>
      <Tabs items={tabs} id={`block-settings-tab-${id}`} />
    </div>
  );
};
